import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useWhoAmIQuery } from "@generated";
import navItems, { NavItem } from "./navItems";
import UserMenuCard from "./UserMenuCard";
import NotificationBellV2 from "./NotificationBellV2";
import scopestackLogo from "../../images/scopestack-logo.png";
// The square mark lives with the Rails assets, so both chromes draw the
// collapsed logo from one file.
import scopestackMark from "../../../../assets/images/logo-only.png";

interface SidebarProps {
  expanded: boolean;
  onToggle: () => void;
  reloadDocument?: boolean;
  clientSideNav?: boolean;
}

const SIDEBAR_EXPANDED_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;

const Sidebar: React.FC<SidebarProps> = ({
  expanded,
  onToggle,
  reloadDocument = false,
  clientSideNav = false,
}) => {
  const location = useLocation();
  const { data: me } = useWhoAmIQuery();
  const isSiteAdmin = me?.data?.attributes?.["site-admin"] === true;

  const visibleNavItems = navItems.filter(
    (item) => !item.siteAdminOnly || isSiteAdmin
  );

  const isActive = (item: NavItem) => {
    if (item.path === "/home") {
      return location.pathname === "/home" || location.pathname === "/";
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        width: expanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
        backgroundColor: "#1C2655",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.2s ease",
        overflow: "hidden",
        zIndex: 1000,
      }}
    >
      {/* Logo area */}
      <div
        style={{
          padding: expanded ? "20px 16px 12px" : "20px 0 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "flex-start" : "center",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        {expanded ? (
          <img
            src={scopestackLogo}
            alt="ScopeStack"
            data-testid="sidebar-logo-wordmark"
            style={{ height: 28, objectFit: "contain" }}
          />
        ) : (
          <img
            src={scopestackMark}
            alt="ScopeStack"
            data-testid="sidebar-logo-mark"
            style={{
              height: 32,
              width: 32,
              objectFit: "contain",
            }}
          />
        )}
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, padding: "8px 0", overflowY: "auto" }}>
        {visibleNavItems.map((item) => {
          const active = isActive(item);
          const link = (
            <NavLink
              key={item.id}
              to={item.path}
              id={item.id}
              reloadDocument={reloadDocument || Boolean(item.reloadDocument)}
              style={{
                color: active ? "#63C4AB" : "rgba(255,255,255,0.7)",
                backgroundColor: active
                  ? "rgba(99, 196, 171, 0.1)"
                  : "transparent",
                padding: expanded ? "10px 16px" : "10px 0",
                display: "flex",
                alignItems: "center",
                justifyContent: expanded ? "flex-start" : "center",
                gap: expanded ? 12 : 0,
                borderRadius: 4,
                margin: "2px 8px",
                textDecoration: "none",
                whiteSpace: "nowrap",
                fontSize: 14,
                fontFamily: "Open Sans, sans-serif",
                transition: "background-color 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    "rgba(255,255,255,0.05)";
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    "transparent";
                }
              }}
            >
              <FontAwesomeIcon
                icon={item.icon}
                style={{
                  fontSize: expanded ? 16 : 20,
                  width: expanded ? 16 : 20,
                  flexShrink: 0,
                }}
              />
              {expanded && <span>{item.label}</span>}
            </NavLink>
          );

          const row = expanded ? (
            link
          ) : (
            <OverlayTrigger
              key={item.id}
              placement="right"
              overlay={
                <Tooltip id={`tooltip-${item.id}`}>{item.label}</Tooltip>
              }
            >
              {link}
            </OverlayTrigger>
          );

          if (item.id === "nav-projects") {
            return (
              <React.Fragment key={item.id}>
                <NotificationBellV2 expanded={expanded} />
                {row}
              </React.Fragment>
            );
          }

          return row;
        })}
      </nav>

      {/* User menu card */}
      <UserMenuCard
        expanded={expanded}
        onToggle={onToggle}
        clientSideNav={clientSideNav}
      />
    </div>
  );
};

export { SIDEBAR_EXPANDED_WIDTH, SIDEBAR_COLLAPSED_WIDTH };
export default Sidebar;
